import { ENTITLEMENTS as APPENTITLEMENTS } from '../../core/utils/constants';
import MemoryIcon from '@material-ui/icons/Memory';
import Errored from '../../core/Errored';
import ActionText from '../ActionText';
import Loading from '../../core/Loading';
import Upgrade from '../Upgrade';
import GS from '../../grafsync/grafsync';
import { Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { globalStyles } from '../style/gstheme';
import VersionDetail from './VersionDetail';
import { makeCancelable } from '../../core/utils/utils';
import TakeSnapshotDialog from '../snapshot/TakeSnapshotDialog';

const styles = () => {
  return Object.assign({
    versions: {
      marginRight: 30,
      marginBottom: 30,
      backgroundColor: '#ffffff',
      borderRadius: 4,
      overflow: 'auto',
      padding: 12
    }
  }, globalStyles);
};

class VersionManager extends Component {

  constructor(props) {
    super(props);
    this.state = {
      configurationIRI: null,
      isLoading: true,
      lastSnapshot: 0,
      isViewOnly: !props.entitlements.includes(APPENTITLEMENTS.MANAGE_SNAPSHOTS),
      isErrored: null,
      updatedAt: Date.now(),
      usable: GS.getActiveSubscriptionType() === "Managed" || GS.getActiveSubscriptionType() === "Trial" || GS.getActiveSubscriptionType() === "Premium"
    };
    this.cancellable = null;
  }

  componentDidMount() {
    this.getData();
  }

  setLastSnapshot = (lastSnapshot) => this.setState({ lastSnapshot });

  getData = async () => {
    try {
      GS.handle("get-data");
      const users = await this.doGetUsers().promise;
      const configurations = this.props.entitlements.includes(APPENTITLEMENTS.MANAGE_SNAPSHOTS) ?
        await this.doGetConfigurations().promise
        :
        [];
      const configuration = configurations.find(configuration => configuration.project === this.props.project);
      if (this.cancellable && this.cancellable.isCancelled()) return;
      this.setState({
        users,
        ...(configuration) && { configurationIRI: configuration.id },
        isLoading: false
      });
    }
    catch (e) {
      if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
    }
  };

  doGetUsers() {
    this.cancellable = makeCancelable(GS.subscriptionUsers());
    return this.cancellable;
  }

  doGetConfigurations() {
    this.cancellable = makeCancelable(GS.snapshotConfigurations());
    return this.cancellable;
  }

  doTakeSnapshot(id, payload) {
    this.cancellable = makeCancelable(GS.takeSnapshot(id, payload));
    return this.cancellable;
  }

  doSearch(payload) {
    this.cancellable = makeCancelable(GS.search(payload));
    return this.cancellable;
  }

  loadVersions = async (page = 1) => {
    GS.handle("loading-versions-vm");
    const data = await this.doSearch({
      search: [
        {
          predicate: "https://grafsync.graphifi.com/schema/gs/isVersionOfProject",
          value: this.props.project
        },
        {
          predicate: "https://grafsync.graphifi.com/schema/gs/isValidVersion"
        }
      ],
      page,
      pageSize: 5,
      basicSort: true,
      sortOrder: "Descending",
      sort: ["https://grafsync.graphifi.com/schema/gs/snapshotTime"]
    }).promise;
    return data;
  };

  checkVersion() {
    this.check = setTimeout(async () => {
      clearTimeout(this.check);
      try {
        const results = await this.loadVersions();
        if (this.cancellable && this.cancellable.isCancelled()) return;
        if (results && results.results.some(pr => pr.id === this.state.snapshotProject)) {
          this.setState({ checkingVersion: false });
        }
        else {
          if (this.cancellable && this.cancellable.isCancelled()) return;
          this.checkVersion();
        }
      }
      catch (e) {
        if (this.cancellable && this.cancellable.isCancelled()) return;
        this.setState({ checkingVersion: false, isErrored: e });
      }
    }, 1000);
  }

  takeSnapshot = async (payload) => {
    try {
      const response = await this.doTakeSnapshot(this.state.configurationIRI, payload).promise;
      const responseData = response ? await response.json() : {};
      const snapshotProject = responseData.project;
      if (snapshotProject) {
        this.setState({ checkingVersion: true, snapshotProject, lastSnapshot: Date.now() }, () => this.checkVersion());
      }
      else {
        this.setState({
          isErrored: {
            message: "Cannot take snapshot for version."
          }
        });
      }
    }
    catch (e) {
      if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
    }
  };

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
  }

  render() {

    const { classes, data, projectType, exportName, historyOption, entitlements, graph } = this.props;
    const { usable, isLoading, configurationIRI, isErrored, checkingVersion, users, lastSnapshot, isViewOnly } = this.state;
    const currentTime = Date.now();

    if (!usable) return <div data-test="no-access" style={{ paddingTop: 60, paddingRight: 30, width: '100%', textAlign: 'center' }}>
      <Typography align="center" variant="body2">Versions are not available with your current subscription.</Typography>
      <Typography align="center" variant="body2">This feature requires a minimum of a Premium subscription.</Typography>
      <Upgrade />
    </div>;

    if (isErrored) return <Errored dataTest="vm-error" data={isErrored} />;
    if (isLoading) return <Loading />;

    return (
      <div style={{ paddingBottom: 30, paddingRight: 30, maxWidth: 1400 }}>
        <div style={{ display: 'flex' }}>
          <div style={{ flex: 1, marginTop: 20 }}>
            <Typography className={classes.subMainHeading}>Project Versions</Typography>
          </div>
          {configurationIRI &&
            <div style={{ marginBottom: 4 }}>
              {!checkingVersion && lastSnapshot < currentTime - parseInt(process.env.REACT_APP_SNAPSHOT_DELAY) &&
                entitlements.includes(APPENTITLEMENTS.MANAGE_SNAPSHOTS) &&
                <TakeSnapshotDialog button title="Create version" onTakeSnapshot={this.takeSnapshot} isVersion={true} />
              }
            </div>
          }
        </div>
        {configurationIRI || isViewOnly ?
          checkingVersion ?
            <div style={{ backgroundColor: '#ffffff', padding: 8, borderRadius: 4 }}>
              <div style={{ height: 300, textAlign: 'center', overflow: 'auto' }}>
                <MemoryIcon style={{ marginTop: 40, width: 100, height: 100, color: '#97d700' }} />
                <ActionText offset="20px">Please wait. Generating version.</ActionText>
              </div>
            </div>
            :
            <VersionDetail
              lastSnapshot={this.setLastSnapshot}
              users={users}
              entitlements={entitlements}
              historyOption={historyOption}
              projectIRI={data.id}
              projectGraph={graph}
              projectType={projectType}
              exportName={exportName} />
          :
          <div style={{ backgroundColor: '#ffffff', padding: 8, borderRadius: 4 }}>
            <div style={{ height: 120 }}>
              <ActionText dataTest="no-snapshot" offset="100px">Please set up snapshots for this project to enable versioning.</ActionText>
            </div>
          </div>
        }
      </div>
    );
  }
}

VersionManager.propTypes = {
  classes: PropTypes.object,
  data: PropTypes.object.isRequired,
  project: PropTypes.string.isRequired,
  projectType: PropTypes.string.isRequired,
  exportName: PropTypes.string.isRequired,
  historyOption: PropTypes.object.isRequired,
  entitlements: PropTypes.array.isRequired,
  graph: PropTypes.string.isRequired
};

export default withStyles(styles)(VersionManager);